import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiClock, FiHeart, FiShield, FiTool, FiStar, FiTarget } = FiIcons;

const About = () => {
  const stats = [
    { icon: FiUsers, number: '5000+', label: 'Happy Customers' },
    { icon: FiClock, number: '10+', label: 'Years Experience' },
    { icon: FiTool, number: '15000+', label: 'Repairs Completed' },
    { icon: FiAward, number: '100%', label: 'Satisfaction Rate' }
  ];

  const values = [
    {
      icon: FiHeart,
      title: 'Customer First',
      description: 'Every decision we make is centered around providing the best possible experience for our customers.'
    },
    {
      icon: FiShield,
      title: 'Quality Assurance',
      description: 'We use only genuine parts and follow strict quality standards to ensure lasting repairs and products.'
    },
    {
      icon: FiStar,
      title: 'Excellence',
      description: 'We strive for excellence in every service we provide, from simple accessories to complex repairs.'
    },
    {
      icon: FiTarget,
      title: 'Innovation',
      description: 'We stay updated with the latest technology and repair techniques to serve you better.'
    }
  ];

  const team = [
    {
      name: 'Professional Technician',
      role: 'Lead Repair Specialist',
      image: 'https://i.ibb.co/4nZXKVtn/images-22.jpg',
      description: 'Expert in mobile device repair with certifications from major manufacturers.'
    },
    {
      name: 'Sales Expert',
      role: 'Customer Relations Manager',
      image: 'https://i.ibb.co/zhHt7BpQ/images-23.jpg',
      description: 'Dedicated to helping customers find the perfect mobile solutions for their needs.'
    },
    {
      name: 'Service Coordinator',
      role: 'Operations Manager',
      image: 'https://i.ibb.co/HpMXBGCH/images-24.jpg',
      description: 'Ensures smooth operations and maintains our high service quality standards.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-gray-100 py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl font-bold text-gray-800 mb-6">
              About <span className="primary-red">Global Mobilemart Inc</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Your trusted mobile partner in Toronto, providing premium smartphones, expert repair services, 
              and quality accessories with unmatched customer service since our establishment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Our Story</h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                Global Mobilemart Inc was founded with a simple mission: to provide Toronto residents 
                with the best mobile technology solutions and services. What started as a small repair 
                shop has grown into a trusted destination for all mobile needs.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg">
                We believe that mobile technology should enhance your life, not complicate it. That's why 
                we offer comprehensive services from device sales to expert repairs, all under one roof 
                with the personal touch that makes us different.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg">
                Located at 1760 Eglinton Ave W in Toronto, we've built lasting relationships with our 
                customers through honest service, competitive prices, and genuine care for their mobile needs.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="https://i.ibb.co/pvrLJgbX/images-1.jpg"
                alt="Our Store"
                className="rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              />
              <img
                src="https://i.ibb.co/bjHqKqXM/images-1.png"
                alt="Our Products"
                className="rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 mt-8"
              />
              <img
                src="https://i.ibb.co/N017X4y/images-2.jpg"
                alt="Repair Services"
                className="rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 -mt-8"
              />
              <img
                src="https://i.ibb.co/v6qJvQyT/images-3.jpg"
                alt="Customer Service"
                className="rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary-red text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Our Achievements</h2>
            <p className="text-xl text-red-100 max-w-3xl mx-auto">
              Numbers that reflect our commitment to excellence and customer satisfaction.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <SafeIcon icon={stat.icon} className="text-2xl" />
                </div>
                <h3 className="text-4xl font-bold mb-2">{stat.number}</h3>
                <p className="text-red-100">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Global Mobilemart Inc.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="bg-primary-red text-white w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <SafeIcon icon={value.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced professionals are dedicated to providing you with the best mobile solutions and service.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover shadow-lg"
                />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{member.name}</h3>
                <p className="primary-red font-medium mb-4">{member.role}</p>
                <p className="text-gray-600 leading-relaxed">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose Us?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Here's what makes Global Mobilemart Inc the preferred choice for mobile solutions in Toronto.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-start space-x-4">
                <div className="bg-primary-red text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiAward} className="text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Certified Expertise</h3>
                  <p className="text-gray-600">Our technicians are certified and trained on the latest mobile technologies and repair techniques.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-primary-red text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiShield} className="text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Quality Guarantee</h3>
                  <p className="text-gray-600">We stand behind our work with comprehensive warranties and use only genuine, high-quality parts.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-primary-red text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiClock} className="text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Fast Turnaround</h3>
                  <p className="text-gray-600">Most repairs are completed the same day, and we always keep you informed about the progress.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-primary-red text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiHeart} className="text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Service</h3>
                  <p className="text-gray-600">We treat every customer like family, providing personalized recommendations and honest advice.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="https://i.ibb.co/PvZM0d0y/images-4.jpg"
                alt="Quality Service"
                className="rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              />
              <img
                src="https://i.ibb.co/chBsmZbt/images-5.jpg"
                alt="Expert Repairs"
                className="rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 mt-8"
              />
              <img
                src="https://i.ibb.co/TD98NF4P/images-6.jpg"
                alt="Premium Products"
                className="rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 -mt-8"
              />
              <img
                src="https://i.ibb.co/7JvgXKyr/images-7.jpg"
                alt="Customer Satisfaction"
                className="rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-red text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Experience the Difference?</h2>
            <p className="text-xl text-red-100 mb-8 leading-relaxed">
              Visit Global Mobilemart Inc today and discover why we're Toronto's trusted mobile partner. 
              Our team is ready to help you with all your mobile needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+14374381760"
                className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
              >
                Call Us Today
              </a>
              <a
                href="#"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-all duration-300"
              >
                Visit Our Store
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;